import { Monitor, RotateCcw, Smartphone } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { listBotAudiences, resetBotPlayground, startBotPlayground } from '../../api/endpoints';
import { ErrorNote, Note } from '../../components/ErrorNote';
import { Spinner } from '../../components/Spinner';
import { useLoad } from '../../lib/useLoad';

type Device = 'desktop' | 'mobile';

/**
 * The assistant as a customer meets it: the REAL widget, in the real frame, on
 * a real visitor session — not a mock of it. It starts as the launcher bubble
 * and opens on click, because how the thing looks closed is half of what an
 * operator is trying to judge.
 *
 * That means the answers come back through the ordinary pipeline (ingest → bot
 * queue → outbound), so what you see here is exactly what the customer would
 * get, including the parts a mock would quietly get wrong. The session's
 * contact is marked playground server-side, which is what keeps these turns out
 * of the run budget and out of the team's notifications; they are labelled
 * Playground in Activity.
 *
 * The panel is a plain iframe of /widget.html and sizes itself from the
 * `sphere:size` messages the frame already posts to its host — the same
 * protocol public/widget.js speaks on a tenant's site.
 */
export function BotPlayground({ enabled }: { enabled: boolean }) {
  const session = useLoad(startBotPlayground);
  const audiences = useLoad(listBotAudiences);
  const others = (audiences.data ?? []).filter((row) => !row.isDefault);
  /** Null is the default audience — the frame is then loaded exactly as a customer's is. */
  const [audience, setAudience] = useState<string | null>(null);
  const [device, setDevice] = useState<Device>('desktop');
  const [size, setSize] = useState({ width: 0, height: 0, open: false });
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const [nonce, setNonce] = useState(0);

  const src = useMemo(() => {
    if (!session.data) return null;
    const params = new URLSearchParams({
      wt: session.data.websiteToken,
      vt: session.data.visitorToken,
    });
    // Exactly what public/widget.js puts on the frame's url for a page that
    // names an audience — the point of this panel is that nothing here is a
    // special case.
    if (audience) params.set('audience', audience);
    // The nonce remounts the frame after a reset so it forgets the thread it
    // was showing.
    return `${window.location.origin}/widget.html?${params}&n=${nonce}`;
  }, [session.data, audience, nonce]);

  const onMessage = useCallback((event: MessageEvent) => {
    if (event.origin !== window.location.origin) return;
    if (event.source !== frameRef.current?.contentWindow) return;
    const data = event.data as { type?: string; width?: number; height?: number; open?: boolean };
    if (data?.type === 'sphere:size') {
      setSize({ width: Number(data.width) || 0, height: Number(data.height) || 0, open: !!data.open });
    }
  }, []);

  useEffect(() => {
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [onMessage]);

  const reset = async () => {
    setResetting(true);
    setError(null);
    try {
      await resetBotPlayground();
      setSize({ width: 0, height: 0, open: false });
      setNonce((n) => n + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not clear the test conversation.');
    } finally {
      setResetting(false);
    }
  };

  /**
   * Switching who you are testing as starts a fresh thread, because the
   * audience is stamped on the conversation when it opens: carrying on in the
   * old one would keep answering as the old persona and read as a bug in the
   * feature being tested.
   */
  const chooseAudience = async (next: string | null) => {
    if (next === audience) return;
    setAudience(next);
    await reset();
  };

  const stageHeight = device === 'mobile' ? 620 : 520;
  // Mirrors public/widget.js: the frame reports the size it wants, the host
  // gives it that box in the corner, and a mobile-width host goes full-bleed
  // when the panel is open.
  const panel = size.open && device === 'mobile'
    ? { width: '100%', height: '100%', right: 0, bottom: 0 }
    : {
        width: `${size.width || 96}px`,
        height: `${Math.min(size.height || 96, stageHeight - 32)}px`,
        right: '16px',
        bottom: '16px',
      };

  return (
    <section className="card card-pad manage-card">
      <div className="manage-card-head">
        <div>
          <h2 className="manage-card-title">Playground</h2>
          <p className="manage-card-sub">
            The real widget, on a test conversation only you can see. Nothing here counts against
            your run budget and nobody on your team is notified.
          </p>
        </div>
        <div className="bot-play-tools">
          {others.length > 0 && (
            <div className="bot-device-switch">
              <button
                type="button"
                className={audience ? '' : 'is-on'}
                disabled={resetting}
                onClick={() => void chooseAudience(null)}
              >
                Customers
              </button>
              {others.map((row) => (
                <button
                  key={row.id}
                  type="button"
                  className={audience === row.id ? 'is-on' : ''}
                  disabled={resetting}
                  onClick={() => void chooseAudience(row.id)}
                >
                  {row.label}
                </button>
              ))}
            </div>
          )}
          <div className="bot-device-switch">
            <button
              type="button"
              className={device === 'desktop' ? 'is-on' : ''}
              onClick={() => setDevice('desktop')}
              aria-label="Desktop"
            >
              <Monitor size={14} />
            </button>
            <button
              type="button"
              className={device === 'mobile' ? 'is-on' : ''}
              onClick={() => setDevice('mobile')}
              aria-label="Mobile"
            >
              <Smartphone size={14} />
            </button>
          </div>
          <button className="btn btn-ghost" onClick={() => void reset()} disabled={resetting}>
            {resetting ? <Spinner /> : <RotateCcw size={14} />}
            Start over
          </button>
        </div>
      </div>

      {error && <ErrorNote error={error} />}
      {!enabled && (
        <Note tone="info">
          The assistant is switched off, so it is not answering real conversations — but it answers
          here, which is the point of trying it first.
        </Note>
      )}
      {session.data && !session.data.answers && (
        <Note tone="warn">
          The widget below is <strong>{session.data.inboxName}</strong>, which the assistant does not
          answer — <strong>Install</strong> lets it answer there in one press, or nothing will reply.
        </Note>
      )}

      {session.loading ? (
        <div className="home-loading">
          <Spinner large />
        </div>
      ) : session.error ? (
        <ErrorNote error={session.error} />
      ) : !src ? null : (
        <div className={`bot-stage bot-stage-${device}`} style={{ height: stageHeight }}>
          <div className="bot-stage-chrome">
            <span />
            <span />
            <span />
            <em>{device === 'mobile' ? 'your site on a phone' : 'yoursite.com'}</em>
          </div>
          <div className="bot-stage-page">
            <div className="bot-stage-skeleton" aria-hidden="true">
              <div />
              <div />
              <div />
            </div>
            <iframe
              ref={frameRef}
              title="Chat widget preview"
              src={src}
              className="bot-stage-widget"
              style={panel}
            />
          </div>
        </div>
      )}
    </section>
  );
}
